/**
 * What people actually type, mapped to the category that answers it.
 *
 * Every search box used to match the typed word against the category name and
 * nothing else, so "plumber" found nothing at all while Plumbing sat right
 * there, and "mechanic" missed Automotive entirely. Glenwood's first member hit
 * exactly that and asked where the service types had gone. Renaming the
 * categories fixes the common case (052); this fixes the rest of the words,
 * including the ones we chose against — somebody will always type "plumbing".
 *
 * Keyed by slug, so renaming a category never breaks its synonyms. Add to it
 * freely: a word that matches nothing costs nothing, and a missing word costs a
 * member a dead end.
 */
export const CATEGORY_SYNONYMS = {
  accounting: ["accountant", "tax", "tax return", "bas", "cpa", "audit"],
  "air-conditioning": ["aircon", "air con", "split system", "cooling", "heating", "hvac", "refrigeration"],
  "appliance-repair": ["appliance", "fridge", "washing machine", "dishwasher", "oven", "whitegoods", "dryer"],
  automotive: [
    "mechanic", "mobile mechanic", "car repair", "car service", "auto", "auto electrical",
    "tyres", "rego", "roadworthy", "rwc", "brakes", "clutch", "transmission", "radiator",
    "panel beater", "smash repair", "windscreen", "logbook service", "exhaust", "4wd",
  ],
  beauty: ["beautician", "nails", "waxing", "facial", "lashes", "brows", "spray tan", "makeup"],
  "bobcat-tipper-hire": ["bobcat", "tipper", "dingo", "posi track", "skid steer", "machine hire"],
  bookkeeping: ["bookkeeper", "payroll", "xero", "myob", "accounts", "invoicing"],
  butcher: ["butchery", "meat", "smallgoods", "sausages", "beef", "lamb"],
  cafe: ["coffee", "barista", "breakfast", "brunch", "milkshake"],
  carpentry: ["carpenter", "chippy", "cabinet maker", "joinery", "decking", "deck", "pergola", "timber", "shelving"],
  catering: ["caterer", "spit roast", "function food", "party food", "platters"],
  chiropractic: ["chiro", "chiropractor", "back pain", "neck pain", "adjustment"],
  cleaning: [
    "cleaner", "house cleaning", "bond clean", "end of lease", "carpet cleaning",
    "window cleaning", "pressure washing", "high pressure", "spring clean",
  ],
  concreting: ["concreter", "concrete", "slab", "driveway", "exposed aggregate", "shed slab", "footings"],
  earthmoving: ["excavation", "excavator", "digger", "earthworks", "trenching", "land clearing", "dam"],
  electrical: ["electrician", "sparky", "power point", "switchboard", "wiring", "rewire", "lights", "ceiling fan", "safety switch"],
  "farm-services": ["farm", "agricultural", "ag", "hay", "cattle", "fodder", "tractor", "silage", "spraying", "livestock transport"],
  fencing: ["fencer", "fence", "colorbond", "pool fence", "rural fencing", "post and rail", "gates", "barbed wire"],
  "financial-planning": ["financial planner", "financial adviser", "financial advisor", "super", "superannuation", "insurance", "retirement"],
  fitness: ["gym", "personal trainer", "pt", "pilates", "yoga", "boot camp", "training"],
  flooring: ["floors", "timber floor", "carpet", "vinyl", "laminate", "floor sanding", "polished concrete"],
  gardening: ["gardener", "weeding", "pruning", "hedging", "garden maintenance", "mulching", "green waste"],
  hairdressing: ["hairdresser", "hair", "barber", "haircut", "colour", "salon", "blow wave"],
  handyman: ["odd jobs", "small jobs", "repairs", "maintenance", "flat pack", "hanging doors"],
  "it-support": ["computer repair", "computer", "laptop", "wifi", "network", "printer", "virus", "backup", "internet"],
  landscaping: ["landscaper", "retaining wall", "turf", "lawn laying", "paving", "garden design", "irrigation", "sprinklers"],
  locksmith: ["locks", "keys", "key cutting", "lockout", "rekey", "deadlock", "safe"],
  medical: ["doctor", "gp", "clinic", "medical centre", "bulk billing", "skin check", "vaccination"],
  mowing: ["mower", "lawn", "lawn mowing", "slashing", "whipper snipper", "yard", "acreage mowing"],
  painting: ["painter", "house painting", "interior painting", "spray painting", "decorating", "repaint"],
  "pest-control": ["pest", "termites", "termite inspection", "cockroach", "ants", "spiders", "rodents", "mice", "fumigation", "wasps"],
  "pet-services": ["dog grooming", "groomer", "dog wash", "pet sitting", "boarding", "kennel", "cattery", "dog walking", "dog training"],
  pharmacy: ["chemist", "prescription", "script", "medication", "pharmacist", "webster pack"],
  photography: ["photographer", "photos", "wedding photographer", "portraits", "drone photography", "real estate photography"],
  physiotherapy: ["physio", "physiotherapist", "rehab", "sports injury", "dry needling", "exercise physiology"],
  plastering: ["plasterer", "gyprock", "plasterboard", "cornice", "render", "rendering", "patching", "ceiling repair"],
  plumbing: [
    "plumber", "blocked drain", "hot water", "leak", "leaking tap", "tap", "toilet",
    "burst pipe", "gasfitter", "gas fitting", "drainage", "septic", "water tank", "sewer",
  ],
  "pool-spa-services": ["pool", "spa", "pool cleaning", "pool pump", "chlorine", "pool service"],
  "pub-bar": ["pub", "bar", "hotel", "tavern", "beer", "counter meal"],
  "real-estate": ["real estate agent", "realtor", "property", "rental", "rentals", "house for sale", "property management", "appraisal"],
  restaurant: ["dinner", "takeaway", "food", "meals", "eat out", "pizza", "chinese", "thai"],
  roofing: ["roofer", "roof", "roof repair", "re-roof", "gutters", "guttering", "downpipes", "metal roof", "roof tiles", "leaking roof"],
  security: ["security guard", "alarm", "cctv", "cameras", "patrol", "monitoring", "crowd control"],
  "skip-bin-hire": ["skip", "skip bin", "rubbish removal", "waste", "bin hire", "green waste", "junk removal"],
  "solar-installation": ["solar", "solar panels", "battery", "batteries", "inverter", "off grid", "solar hot water"],
  tiling: ["tiler", "tiles", "bathroom tiling", "waterproofing", "grout", "splashback", "regrout"],
  "tree-services": ["tree lopper", "lopping", "arborist", "tree removal", "stump grinding", "tree trimming", "mulching", "storm damage"],
  tutoring: ["tutor", "maths", "english", "naplan", "homework help", "reading", "exam prep"],
  veterinary: ["vet", "animal", "dog", "cat", "horse", "livestock", "vaccination", "desexing"],
  welding: ["welder", "fabrication", "steel", "boilermaker", "metal work", "on site welding", "alloy", "gates and grilles"],
};

// Two characters is too little to mean anything and matches half the list.
const MIN_QUERY = 2;
// Below this, a word is too short to hunt for inside a listing's description —
// "ag" would hit "agency" and "car" would hit "caravan".
const MIN_TERM_FOR_TEXT = 4;

const slugOf = (category) => category?.slug || category?.id || "";

// How much longer than the synonym a query may be and still count as the same
// word. Two characters covers the plurals people type — "plumbers", "roofers".
const MAX_PLURAL_SUFFIX = 2;

const wordMatchesQuery = (word, query) => {
  // Match from the start of the synonym or the start of any word inside it, not
  // just anywhere in the string. Plain "includes" matched mid-word, so "ice"
  // pulled up "pool service" and every other trade with "service" in it.
  if (word.startsWith(query)) return true;
  if (word.split(" ").some((part) => part.startsWith(query))) return true;
  // Matching the other way round has to stay tight. The loose version — does the
  // query contain the word anywhere — let a short synonym swallow a longer word:
  // "spa" is inside "sparky", so searching for an electrician returned Pool & Spa
  // Services. A plural of a reasonably long word is all this needs to allow.
  return (
    word.length >= MIN_TERM_FOR_TEXT &&
    query.length > word.length &&
    query.length - word.length <= MAX_PLURAL_SUFFIX &&
    query.startsWith(word)
  );
};

/**
 * Should this category be offered to someone who has typed `rawQuery`?
 * Matches the category's name, and anything in its synonym list.
 */
export const categoryMatchesQuery = (category, rawQuery) => {
  const query = (rawQuery || "").toLowerCase().trim();
  if (query.length < MIN_QUERY) return false;

  // The name is held to the same rule as the synonyms. It used to be a bare
  // substring test, which meant "ice" returned Farm Services, Pet Services, Pool
  // & Spa Services and Tree Services — everything with "service" in its name.
  if (wordMatchesQuery((category?.name || "").toLowerCase(), query)) return true;

  // The slug is the old name. Renaming Plumbing to Plumber must not make
  // "plumbing" a dead end — the word every one of these categories used to be
  // called still has to find it, and the slug is exactly that word.
  const slug = slugOf(category).toLowerCase().replace(/-/g, " ");
  if (slug && wordMatchesQuery(slug, query)) return true;

  return (CATEGORY_SYNONYMS[slugOf(category)] || []).some((word) =>
    wordMatchesQuery(word, query),
  );
};

/**
 * The same test, for the `{ id, label }` shape `SelectionListModal` works in.
 * Pass it as that component's `matchOption` on any list of categories.
 *
 * The plain prefix check on the end is for the first letter typed: the synonym
 * test ignores anything shorter than two characters, and a picker should still
 * narrow as soon as you start typing.
 */
export const matchCategoryOption = (option, query) =>
  categoryMatchesQuery({ slug: option?.id, name: option?.label }, query) ||
  (option?.label || "").toLowerCase().startsWith(query);

/**
 * The typed words plus the ones that mean the same thing, for searching listing
 * text. Someone inside the Plumber category who types "plumber" should still see
 * "AC Plumbing Qld" — the trade word is what the business called itself.
 */
export const relatedSearchTerms = (rawQuery) => {
  const query = (rawQuery || "").toLowerCase().trim();
  if (query.length < MIN_QUERY) return query ? [query] : [];

  const terms = new Set([query]);
  Object.entries(CATEGORY_SYNONYMS).forEach(([slug, words]) => {
    const family = [slug.replace(/-/g, " "), ...words];
    if (!family.some((word) => wordMatchesQuery(word, query))) return;
    family.forEach((word) => {
      if (word.length >= MIN_TERM_FOR_TEXT) terms.add(word);
    });
  });

  return [...terms];
};

export default CATEGORY_SYNONYMS;
